import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { ArrowDown, ArrowUp, Eye, EyeOff, RotateCcw, Settings2 } from 'lucide-react';
import type { Widget } from '@mnc/contracts';
import { useAuth } from '../../lib/auth';
import { usePreferences } from '../../lib/prefs';
import { describeError } from '../../lib/errors';
import { pick } from '../../i18n';
import { Skeleton, cn } from '../../components/ui';
import { WIDGETS, WidgetError } from './widgets';

const SPAN: Record<Widget['size'], string> = { sm: 'lg:col-span-2', md: 'lg:col-span-3', lg: 'lg:col-span-6' };

/** The user's arrangement applied to the widgets they may see: saved order first, then manifest order for new ones. */
export function arrange(widgets: Widget[], order: string[]): Widget[] {
  const rank = new Map(order.map((code, i) => [code, i]));
  return [...widgets].sort((a, b) => (rank.get(a.code) ?? 1000 + a.sort_order) - (rank.get(b.code) ?? 1000 + b.sort_order));
}

function WidgetCard({ widget, editing, hidden, first, last, onToggle, onMove }: { widget: Widget; editing: boolean; hidden: boolean; first: boolean; last: boolean; onToggle: () => void; onMove: (delta: -1 | 1) => void }) {
  const { t, i18n } = useTranslation();
  // The grid only renders widgets that have a component, and a card keeps its code for life, so this hook call is stable.
  const useWidget = WIDGETS[widget.code]!;
  const state = useWidget();
  const title = pick(i18n.language, widget.name_en, widget.name_ar);
  return (
    <section className={cn('card flex flex-col p-4', SPAN[widget.size], hidden && 'opacity-50')} aria-label={title}>
      <header className="mb-3 flex items-center justify-between gap-2">
        <h2 className="min-w-0 truncate text-sm font-semibold" dir="auto">
          {editing ? title : <Link to={state.to} className="hover:underline">{title}</Link>}
        </h2>
        {editing && (
          <div className="flex shrink-0 items-center gap-1">
            <button type="button" className="btn-secondary px-1.5 py-1" disabled={first} onClick={() => onMove(-1)} title={t('dashboard.moveUp')} aria-label={t('dashboard.moveUp')}><ArrowUp size={14} /></button>
            <button type="button" className="btn-secondary px-1.5 py-1" disabled={last} onClick={() => onMove(1)} title={t('dashboard.moveDown')} aria-label={t('dashboard.moveDown')}><ArrowDown size={14} /></button>
            <button type="button" className="btn-secondary px-1.5 py-1" onClick={onToggle} title={t(hidden ? 'dashboard.show' : 'dashboard.hide')} aria-label={t(hidden ? 'dashboard.show' : 'dashboard.hide')} aria-pressed={!hidden}>
              {hidden ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        )}
      </header>
      <div className="min-h-16 flex-1">
        {state.loading ? <div className="space-y-2"><Skeleton className="h-10 w-full" /><Skeleton className="h-4 w-2/3" /><Skeleton className="h-4 w-1/2" /></div> : state.error ? <WidgetError retry={state.retry} /> : state.children}
      </div>
    </section>
  );
}

/**
 * The dashboard (phase-1-scope §1.1): one home page for everyone. The widgets come from the manifests of the
 * enabled modules, already filtered by the reader's page permissions (`/api/config`), so a restricted user simply
 * sees fewer of them — there is no separate "limited home". Order and hidden widgets are saved per user on the
 * server and follow them across devices.
 */
export function Dashboard() {
  const { t, i18n } = useTranslation();
  const { config } = useAuth();
  const { prefs, update } = usePreferences();
  const [editing, setEditing] = useState(false);
  const available = useMemo(() => (config?.widgets ?? []).filter((w) => WIDGETS[w.code]), [config]);
  const hidden = useMemo(() => new Set(prefs?.dashboard.hidden ?? []), [prefs]);
  const ordered = useMemo(() => arrange(available, prefs?.dashboard.order ?? []), [available, prefs]);
  const shown = editing ? ordered : ordered.filter((w) => !hidden.has(w.code));

  const save = (next: { hidden: string[]; order: string[] }) => {
    // Only codes that still exist are stored, so a widget removed by a release never lingers in the preferences.
    const known = new Set(available.map((w) => w.code));
    update({ dashboard: { hidden: next.hidden.filter((c) => known.has(c)), order: next.order.filter((c) => known.has(c)) } }).catch((e: unknown) => toast.error(describeError(e, t)));
  };
  const toggle = (code: string) => {
    const next = new Set(hidden);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    save({ hidden: [...next], order: ordered.map((w) => w.code) });
  };
  const move = (code: string, delta: -1 | 1) => {
    const codes = ordered.map((w) => w.code);
    const i = codes.indexOf(code);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= codes.length) return;
    [codes[i], codes[j]] = [codes[j]!, codes[i]!];
    save({ hidden: [...hidden], order: codes });
  };

  // No widgets for this reader: show their pages as cards (the one data-driven idea of MRLCO's "limited home", kept
  // without a special group or a second home page).
  if (available.length === 0) {
    const pages = (config?.navigation ?? []).flatMap((s) => s.items).filter((p) => p.route !== '/');
    return (
      <div>
        <p className="mb-3 text-sm text-neutral-500">{t('dashboard.noWidgets')}</p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {pages.map((p) => (
            <Link key={p.code} to={p.route} className="card block p-4 font-medium transition hover:border-brand-500" dir="auto">
              {pick(i18n.language, p.name_en, p.name_ar)}
            </Link>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-end gap-2">
        {editing && <span className="me-auto text-xs text-neutral-500">{t('dashboard.editHint')}</span>}
        {editing && (
          <button type="button" className="btn-secondary px-2 py-1 text-xs" onClick={() => save({ hidden: [], order: [] })}>
            <RotateCcw size={14} /> {t('dashboard.reset')}
          </button>
        )}
        <button type="button" className={cn(editing ? 'btn-primary' : 'btn-secondary', 'px-2 py-1 text-xs')} onClick={() => setEditing((v) => !v)} aria-pressed={editing}>
          <Settings2 size={14} /> {t(editing ? 'dashboard.done' : 'dashboard.customise')}
        </button>
      </div>
      {shown.length === 0 ? (
        <p className="card p-6 text-center text-sm text-neutral-500">{t('dashboard.allHidden')}</p>
      ) : (
        <div className="grid gap-4 lg:grid-cols-6">
          {shown.map((w, i) => (
            <WidgetCard key={w.code} widget={w} editing={editing} hidden={hidden.has(w.code)} first={i === 0} last={i === shown.length - 1} onToggle={() => toggle(w.code)} onMove={(d) => move(w.code, d)} />
          ))}
        </div>
      )}
    </div>
  );
}
